import type { FC } from "react";

import { motion } from "framer-motion";

import { formatAbbrevNumber, formatUsd } from "../../utils/currency";
import { SIGNAL_HEX } from "../common/colorTokens";
import { ActorLevelChip } from "./ActorLevelChip";
import {
  type BlackboardInstance,
  getInstanceDisplayState,
  getInstanceDisplayStyle,
  wakeBlockBadgeLabel,
} from "./blackboardModels";
import { InstanceActionMenu } from "./InstanceActionMenu";
import { getAvatarBackgroundColor, InstanceAvatar } from "./InstanceAvatar";
import { CornerBracket, statusMeta } from "./InstanceHeaderBadges";
import { InstanceStatCell } from "./InstanceStatCell";
import { ModelChip } from "./ModelChip";
import { ReasoningEffortChip } from "./ReasoningEffortChip";

interface InstanceDrawerHeaderProps {
  instance: BlackboardInstance;
  burnRateUsdPerHr: number | null;
  /** Live LLM-activity flag — folded into the status chip so the drawer reads
   *  the same operator-facing state ("Generating" etc.) as the fleet card,
   *  instead of the raw lifecycle label. */
  isGenerating: boolean;
  onClose: () => void;
  /** When set (e.g. "‹ Back" for an overlay), the close control renders as a
   *  labelled button instead of the bare × icon. */
  closeLabel?: string;
  onKill: () => void;
  killing: boolean;
  onLifecycleChange?: () => void;
}

export const InstanceDrawerHeader: FC<InstanceDrawerHeaderProps> = ({
  instance,
  burnRateUsdPerHr,
  isGenerating,
  onClose,
  closeLabel,
  onKill,
  killing,
  onLifecycleChange,
}) => {
  // Durable-status chrome (top gradient, BURN accent) stays keyed to the raw
  // lifecycle hue; the status *chip* uses the derived display state so it reads
  // identically to the card.
  const meta = statusMeta[instance.status];
  const displayState = getInstanceDisplayState(instance, isGenerating);
  const displayStyle = getInstanceDisplayStyle(displayState);
  const wakeBlockBadge = wakeBlockBadgeLabel(instance);
  const accent = getAvatarBackgroundColor(instance.avatarSeed);
  const cacheHitPct = instance.lastCacheStats?.cacheHitPct ?? null;
  const ctx = instance.contextWindow;
  const ctxFill =
    ctx && instance.overrides
      ? Math.min(
          100,
          (ctx.contextWindowSize / instance.overrides.contextWindowTokens) *
            100,
        )
      : 0;

  return (
    <div
      className="relative flex flex-shrink-0 flex-col border-b border-emerald-500/30 bg-slate-50 px-4 pb-3 pt-3 dark:border-emerald-400/20 dark:bg-slate-950"
      style={{
        backgroundImage: `linear-gradient(rgba(${hexToRgb(accent)},0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(${hexToRgb(accent)},0.06) 1px, transparent 1px)`,
        backgroundSize: "20px 20px",
      }}
    >
      {/* Scanline overlay */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, rgba(255,255,255,1) 0 1px, transparent 1px 3px)",
        }}
      />

      {/* Top gradient accent, keyed to status */}
      <div
        className="absolute inset-x-0 top-0 h-0.5"
        style={{
          backgroundImage: `linear-gradient(90deg, transparent, ${meta.accent}, transparent)`,
        }}
      />

      {/* Corner brackets */}
      <CornerBracket position="tl" color={accent} />
      <CornerBracket position="tr" color={accent} />

      {/* Top row: bracket label + action menu + close. Lifecycle verbs
          (wake/restart/kill) live in the ⋮ menu so the header carries a
          single, predictable action affordance instead of a standalone Kill
          button competing with the identity row. */}
      <div className="relative flex items-center justify-between">
        <p className="font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-slate-500">
          [ INSTANCE · {instance.id.slice(0, 8)} ]
        </p>
        <div className="flex items-center gap-1.5">
          <InstanceActionMenu
            instanceId={instance.id}
            status={instance.status}
            killing={killing}
            onKill={onKill}
            onLifecycleChange={onLifecycleChange}
          />
          <button
            type="button"
            onClick={onClose}
            className={`flex h-6 items-center justify-center border border-slate-300 font-mono text-[12px] text-slate-600 hover:border-emerald-400/60 hover:text-emerald-700 dark:border-slate-700 dark:text-slate-400 dark:hover:text-emerald-300 ${
              closeLabel
                ? "px-2 text-[10px] font-bold uppercase tracking-widest"
                : "w-6"
            }`}
            aria-label={closeLabel ?? "Close"}
          >
            {closeLabel ?? "×"}
          </button>
        </div>
      </div>

      {/* Identity row: avatar + name + queued marker. */}
      <div className="relative mt-2 flex items-center gap-3">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ damping: 18, stiffness: 220, type: "spring" }}
          className="relative"
          style={{ filter: `drop-shadow(0 0 12px ${accent}55)` }}
        >
          <div className="sm:hidden">
            <InstanceAvatar
              seed={instance.avatarSeed}
              size="md"
              status={instance.status}
              animated
            />
          </div>
          <div className="hidden sm:block">
            <InstanceAvatar
              seed={instance.avatarSeed}
              size="lg"
              status={instance.status}
              animated
            />
          </div>
        </motion.div>

        <div className="flex min-w-0 flex-1 items-center gap-2">
          <h2 className="truncate font-mono text-base font-semibold tracking-tight text-slate-900 sm:text-lg dark:text-white">
            {instance.displayName}
          </h2>
          {instance.channelLockWaiting && (
            <motion.span
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 1.2, repeat: Infinity }}
              className="font-mono text-[9px] font-bold uppercase tracking-widest text-amber-700 dark:text-amber-300"
            >
              [ QUEUED ]
            </motion.span>
          )}
        </div>
      </div>

      {/* Tag row — full header width, scrolls horizontally on mobile if it
          overflows. Lives outside the identity row's flex context so it
          always has the full drawer width regardless of avatar/kill button. */}
      <div className="relative mt-1.5 flex items-center gap-1.5 overflow-x-auto whitespace-nowrap [-ms-overflow-style:none] [scrollbar-width:none] sm:flex-wrap sm:overflow-visible sm:whitespace-normal [&::-webkit-scrollbar]:hidden">
        <span
          className="inline-flex items-center gap-1.5 border border-slate-300 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest dark:border-slate-700/60"
          title={displayStyle.tooltip ?? undefined}
        >
          {displayStyle.pulseClass ? (
            <span
              aria-hidden
              className={`h-1.5 w-1.5 ${displayStyle.pulseClass}`}
            />
          ) : (
            <span
              aria-hidden
              className="h-1.5 w-1.5 bg-slate-400 dark:bg-slate-600"
            />
          )}
          <span className={displayStyle.textClass}>{displayState}</span>
        </span>
        {instance.modelRef && <ModelChip modelRef={instance.modelRef} />}
        {instance.overrides?.reasoningEffort && (
          <ReasoningEffortChip
            override={instance.overrides.reasoningEffortOverride}
            reasoningEffort={instance.overrides.reasoningEffort}
            source={instance.overrides.reasoningEffortSource}
          />
        )}
        <ActorLevelChip level={instance.evolution} />
        {wakeBlockBadge && (
          <span
            className="inline-flex items-center border border-amber-300 bg-amber-50 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wide text-amber-800 dark:border-amber-400/30 dark:bg-amber-950/40 dark:text-amber-200"
            title={wakeBlockBadge.title}
          >
            {wakeBlockBadge.label}
          </span>
        )}
      </div>

      {/* Stats strip */}
      <div className="relative mt-2 grid grid-cols-4 gap-1 sm:mt-3 sm:gap-2">
        <InstanceStatCell
          label="BURN"
          value={
            burnRateUsdPerHr === null ? "quiet" : formatUsd(burnRateUsdPerHr)
          }
          sub={burnRateUsdPerHr === null ? "no spend" : "/hr"}
          accentColor={meta.accent}
          dim={burnRateUsdPerHr === null}
        />
        <InstanceStatCell
          label="TOTAL"
          value={
            instance.totalCostUsd === null
              ? "—"
              : formatUsd(instance.totalCostUsd)
          }
          sub="lifetime"
          accentColor={SIGNAL_HEX.accent}
        />
        <InstanceStatCell
          label="CACHE"
          value={
            cacheHitPct === null ? "no wakes" : `${Math.round(cacheHitPct)}%`
          }
          sub={cacheSub(cacheHitPct, instance.lastCacheStats?.isBreak ?? false)}
          accentColor={cacheAccent(cacheHitPct)}
          dim={cacheHitPct === null}
        />
        <InstanceStatCell
          label="CTX"
          value={ctx ? formatAbbrevNumber(ctx.contextWindowSize, "exact") : "—"}
          sub={
            instance.overrides
              ? `/ ${formatAbbrevNumber(instance.overrides.contextWindowTokens, "exact")}`
              : undefined
          }
          accentColor={SIGNAL_HEX.info}
          fillPct={ctxFill}
        />
      </div>
    </div>
  );
};

function cacheAccent(pct: number | null): string {
  if (pct === null) {
    return SIGNAL_HEX.neutral;
  }
  if (pct >= 80) {
    return SIGNAL_HEX.healthy;
  }
  if (pct >= 50) {
    return SIGNAL_HEX.warning;
  }
  return SIGNAL_HEX.neutral;
}

function hexToRgb(hex: string): string {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `${r},${g},${b}`;
}

function cacheSub(cacheHitPct: number | null, isBreak: boolean): string {
  if (cacheHitPct === null) {
    return "yet";
  }
  return isBreak ? "break" : "hit";
}
